import React from 'react';
import styled from 'styled-components';
import ButtonOutline from '../../../app/common/components/ButtonOutline';
import OverlayDialog from '../../../app/common/components/OverlayDialog';
import Spinner from '../../../app/common/components/Spinner';
import ICONS from '../../../app/common/constants/icons';
import { COLORS, MQS } from '../../../app/common/constants/styles';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { IAddress } from '../address/address.interface';
import AddressContainer from '../address/AddressContainer';
import AddressFormContainer from '../addressForm/AddressFormContainer';
import { TooltipWrapper } from '../../../app/common/components/TooltipWrapper';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';
import { IAddressService } from '../addressListJP/AddressUpdateInfo';
import {
    destroyChallenge,
    updateChallengerLanguage,
    RenderChallenge,
    startChallenge,
} from '../../../utils/challengerMain';
import OverlayHelper from '../../../utils/overlayHelper';
import { PageId } from '../../../config/constants/pageId.enum';
import HeadTitle from '../../../app/common/components/HeadTitle';

export interface IAddressListProps {
    maxNumberOfAddress: number;
    addressCards: { [key: string]: string[] };
    addresses: IAddress[];
    formOpen?: boolean;
    loading: boolean;
    getAddresses: () => void;
    onDefaultChange: () => void;
    closeOverlay: () => void;
    openOverlay: () => void;
    challengerPid: string;
    locale: SupportedLanguage;
    updatedSuccess?: boolean;
    isJpRegion?: boolean;
    challengerPidDefault: string;
}

export interface IAddressListState {
    isOverlayOpen: boolean;
    selectedAddress?: IAddress;
}

export const AddressListWrapper = styled.div`
    & > button {
        padding: 15px 65px;

        @media (max-width: ${MQS.mq2Min}) {
            padding: 15px 30px;
        }
    }
`;

export const ErrorWrapper = styled.div`
    color: ${COLORS.darkRed};
`;

export const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 36px 0;

    @media (max-width: ${MQS.mq2Min}) {
        margin-top: 20px;
    }
`;

export const SpinnerWrapper = styled.div`
    margin: 50px auto;
    width: 50px;
`;

class AddressList extends React.Component<IAddressListProps, IAddressListState> {
    constructor(props: IAddressListProps) {
        super(props);

        this.state = {
            isOverlayOpen: false,
        };

        this.onOverlayClose = this.onOverlayClose.bind(this);
        this.createAddress = this.createAddress.bind(this);
        this.selectAddress = this.selectAddress.bind(this);
    }

    public componentDidMount() {
        window.scrollTo({ top: 0 });
        const { addresses, challengerPidDefault, loading, getAddresses, challengerPid } =
            this.props;

        if (addresses.length < 1 && !loading) {
            getAddresses();
        }
        startChallenge(challengerPid);
        startChallenge(challengerPidDefault);
    }

    public componentWillUnmount() {
        const { challengerPid, challengerPidDefault } = this.props;
        if (challengerPid) {
            destroyChallenge(challengerPid);
        }
        destroyChallenge(challengerPidDefault);

        OverlayHelper.closeHelper(PageId.address);
    }

    public componentDidUpdate(prevProps: IAddressListProps): void {
        const { formOpen, locale } = this.props;

        if (!formOpen && locale !== prevProps.locale) {
            updateChallengerLanguage(locale);
        }
    }

    private renderChallenge(challengerPid: string) {
        return (
            <div>
                <RenderChallenge cpid={challengerPid} />
            </div>
        );
    }

    public render() {
        const {
            maxNumberOfAddress,
            addressCards,
            closeOverlay,
            addresses,
            formOpen,
            loading,
            locale,
            challengerPid,
            challengerPidDefault,
        } = this.props;
        const { selectedAddress } = this.state;
        let main;
        let addAddressButton;

        if (loading) {
            main = (
                <SpinnerWrapper>
                    <Spinner size={50} color={COLORS.white} qaId="loading-addresses" />
                </SpinnerWrapper>
            );
            addAddressButton = '';
        } else if (addresses) {
            const addressItems = addresses.map((address, index) => (
                <AddressContainer
                    address={address}
                    cards={addressCards[address.addressUuid]}
                    key={address.addressUuid}
                    onEdit={this.selectAddress}
                    isOnlyAddress={addresses && addresses.length === 1}
                    qaId={`${index}`}
                    locale={locale}
                />
            ));
            main = (
                <List data-qa-id="address-list">
                    {addressItems}
                    {challengerPidDefault && this.renderChallenge(challengerPidDefault)}
                </List>
            );
            const buttonId = 'add-address';
            if (addresses.length >= maxNumberOfAddress) {
                addAddressButton = (
                    <TooltipWrapper
                        id="add-address-tooltip"
                        tooltipText={TranslationHelpers.getMessageDescriptor(
                            'tooltip.address.limit_reached',
                        )}
                    >
                        <ButtonOutline
                            id={buttonId}
                            text={TranslationHelpers.getMessageDescriptor(
                                'address.new_limit_reached',
                            )}
                            onClick={this.createAddress}
                            disabled={true}
                            data-qa-id={buttonId}
                        />
                    </TooltipWrapper>
                );
            } else {
                addAddressButton = (
                    <ButtonOutline
                        id={buttonId}
                        text={TranslationHelpers.getMessageDescriptor('address.new')}
                        icon={ICONS.PLUS}
                        onClick={this.createAddress}
                        loading={loading}
                        disabled={loading}
                        data-qa-id={buttonId}
                    />
                );
            }
        } else {
            return '';
        }

        return (
            <>
                <AddressListWrapper>
                    <HeadTitle />
                    {main}
                    {addAddressButton}
                    <OverlayDialog
                        title={TranslationHelpers.getMessageDescriptor(
                            selectedAddress ? 'address.edit' : 'address.new',
                        )}
                        isOpen={formOpen}
                        isLarge={true}
                        onClose={closeOverlay}
                    >
                        <AddressFormContainer
                            isOnlyAddress={!addresses || addresses.length === 0}
                            address={selectedAddress}
                            onSubmit={() => {}}
                        />
                    </OverlayDialog>
                </AddressListWrapper>
            </>
        );
    }

    private createAddress() {
        const { openOverlay } = this.props;
        this.setState({
            selectedAddress: undefined,
        });
        openOverlay();
    }

    private selectAddress(address: IAddress) {
        const { openOverlay } = this.props;
        this.setState({
            selectedAddress: address,
        });
        openOverlay();
    }

    private onOverlayClose() {
        this.setState({
            selectedAddress: undefined,
        });
    }
}

export default AddressList;
